import { type ReactElement, type ReactNode, type Ref, type RefObject } from 'react';
import type { ShopperSearch } from '@/scapi';
import ActiveFilters from '@/components/category-refinements/active-filters';
import CategoryRefinements from '@/components/category-refinements';
import FiltersButton from '@/components/category-refinements/filters-button';
import { ProductResults } from './ui/organisms/product-results';
import type { LoadMoreProps } from './ui/molecules/load-more';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];
type ProductSearchResult = ShopperSearch.schemas['ProductSearchResult'];

export type ProductListingProps = {
    productGridDataKey?: string;
    result: ProductSearchResult;
    refine: string[];
    filtersOpen: boolean;
    onToggleFiltersOpen: () => void;
    selectedFiltersCount: number;
    isProductGridLoading: boolean;
    limit: number;
    handleProductClick?: (product: ProductSearchHit) => void;
    getProductUrl?: (product: ProductSearchHit) => string;
    children?: ReactNode;
    filterAddon?: ReactNode;
    refinementsPanelRef?: RefObject<HTMLDivElement | null>;
    refinementsPanelAriaLabel?: string;
    wrapperClassName?: string;
    appended?: ProductSearchHit[];
    firstNewIndex?: number | null;
    firstNewItemRef?: Ref<HTMLElement>;
    appendPending?: boolean;
    isRestoring?: boolean;
    restorationTarget?: number;
    loadMore?: LoadMoreProps;
    deferredProducts?: Promise<ProductSearchHit[]>;
    deferredSkeletonCount?: number;
};

export const ProductListing = ({
    productGridDataKey,
    result,
    refine,
    filtersOpen,
    onToggleFiltersOpen,
    selectedFiltersCount,
    isProductGridLoading,
    limit,
    handleProductClick,
    getProductUrl,
    children,
    filterAddon,
    refinementsPanelRef,
    refinementsPanelAriaLabel,
    wrapperClassName = 'gap-8',
    appended,
    firstNewIndex,
    firstNewItemRef,
    appendPending,
    isRestoring = false,
    restorationTarget = 0,
    loadMore,
    deferredProducts,
    deferredSkeletonCount = 0,
}: ProductListingProps): ReactElement => {
    const uniqueProducts = (products: ProductSearchHit[]) => {
        const seen = new Set<string>();
        return products.filter((product) => {
            if (seen.has(product.productId)) return false;
            seen.add(product.productId);
            return true;
        });
    };
    const initialProducts = uniqueProducts(result.hits ?? []);
    const appendedProducts = uniqueProducts(appended ?? []);
    const products = uniqueProducts([...initialProducts, ...appendedProducts]);
    const skeletonCount = isRestoring ? restorationTarget : Math.min(limit, Math.max(0, result.total - result.offset));

    return (
        <div className={`flex flex-col lg:flex-row ${wrapperClassName}`}>
            <div className="mb-4 flex flex-col items-start gap-2 lg:hidden" data-slot="filters-wrapper">
                <FiltersButton
                    onClick={onToggleFiltersOpen}
                    isActive={filtersOpen}
                    selectedFiltersCount={selectedFiltersCount}
                />
                {filterAddon}
            </div>

            {filtersOpen && (
                <div
                    ref={refinementsPanelRef}
                    tabIndex={refinementsPanelRef ? -1 : undefined}
                    role={refinementsPanelRef ? 'region' : undefined}
                    aria-label={refinementsPanelAriaLabel}
                    className="w-full lg:w-64 lg:flex-shrink-0 outline-none">
                    <CategoryRefinements result={result} refine={refine} />
                </div>
            )}

            <div className="flex-grow">
                <div className="mb-4 hidden lg:flex lg:items-center lg:gap-4" data-slot="filters-wrapper">
                    <FiltersButton
                        onClick={onToggleFiltersOpen}
                        isActive={filtersOpen}
                        selectedFiltersCount={selectedFiltersCount}
                    />
                    {filterAddon}
                </div>

                <ActiveFilters result={result} />

                {children}
                <ProductResults
                    key={productGridDataKey}
                    products={deferredSkeletonCount > 0 ? initialProducts : products}
                    appendedProducts={deferredSkeletonCount > 0 ? appendedProducts : undefined}
                    skeletonCount={skeletonCount}
                    isGridLoading={isProductGridLoading}
                    isRestoring={isRestoring}
                    isLoadingMore={appendPending}
                    loadMore={loadMore}
                    deferredProducts={deferredProducts}
                    deferredSkeletonCount={deferredSkeletonCount}
                    onProductClick={(product) => handleProductClick?.(product as ProductSearchHit)}
                    getProductUrl={getProductUrl ? (product) => getProductUrl(product as ProductSearchHit) : undefined}
                    firstNewIndex={firstNewIndex}
                    firstNewItemRef={firstNewItemRef}
                />
            </div>
        </div>
    );
};
